"use client"

import * as React from "react"
import { useChat } from "@ai-sdk/react"
import { RotateCcw, Send } from "lucide-react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { Button } from "@/components/ui/button"
import {
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleMessage,
} from "@/components/ui/chat/chat-bubble"
import { ChatInput } from "@/components/ui/chat/chat-input"
import { ChatMessageList } from "@/components/ui/chat/chat-message-list"
import {
  ExpandableChat,
  ExpandableChatBody,
  ExpandableChatFooter,
  ExpandableChatHeader,
} from "@/components/ui/chat/expandable-chat"

import CodeDisplayBlock from "./code-display-block"

export default function ChatSupport() {
  const [isGenerating, setIsGenerating] = React.useState(false)
  const {
    messages,
    setMessages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
  } = useChat({
    onResponse(response) {
      if (response) {
        setIsGenerating(false)
      }
    },
    onError(error) {
      if (error) {
        setIsGenerating(false)
      }
    },
  })

  const messagesRef = React.useRef<HTMLDivElement>(null)
  const formRef = React.useRef<HTMLFormElement>(null)

  React.useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [messages])

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsGenerating(true)
    handleSubmit(e)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (isGenerating || isLoading || !input) return
      setIsGenerating(true)
      onSubmit(e as unknown as React.FormEvent<HTMLFormElement>)
    }
  }

  return (
    <ExpandableChat size="md" icon={"🤖"} position="bottom-right">
      <ExpandableChatHeader className="bg-muted/60 flex-col justify-center gap-2 text-center">
        <h1 className="text-xl font-semibold">
          🤖 <br />
          THK Chat Support
        </h1>
        <p className="text-sm">
          Ask me anything! I&apos;m here to help you with any questions you may
          have.
        </p>
      </ExpandableChatHeader>
      <ExpandableChatBody>
        <ChatMessageList className="bg-muted/25 relative" ref={messagesRef}>
          <ChatBubble variant="received">
            <ChatBubbleAvatar src="" fallback="🤖" />
            <ChatBubbleMessage className="text-sm">
              Hello! I&apos;m the AI assistant. How can I help you today?
            </ChatBubbleMessage>
          </ChatBubble>
          {messages &&
            messages.map((message) => (
              <ChatBubble
                key={message.id}
                variant={message.role == "user" ? "sent" : "received"}
              >
                <ChatBubbleAvatar
                  src=""
                  fallback={message.role == "user" ? "👨🏽" : "🤖"}
                />
                <ChatBubbleMessage
                  className="truncate text-sm"
                  variant={message.role == "user" ? "sent" : "received"}
                >
                  {message.content
                    .split("```")
                    .map((part: string, index: number) => {
                      if (index % 2 === 0) {
                        return (
                          <Markdown key={index} remarkPlugins={[remarkGfm]}>
                            {part}
                          </Markdown>
                        )
                      } else {
                        return (
                          <pre className="pt-2" key={index}>
                            <CodeDisplayBlock code={part} lang="" />
                          </pre>
                        )
                      }
                    })}
                </ChatBubbleMessage>
              </ChatBubble>
            ))}
          {isGenerating && (
            <ChatBubble variant="received">
              <ChatBubbleAvatar src="" fallback="🤖" />
              <ChatBubbleMessage className="text-sm" isLoading />
            </ChatBubble>
          )}
          {messages && messages.length > 0 && (
            <div className="absolute top-2 right-2 flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={
                  messages?.length > 0 ? () => setMessages([]) : undefined
                }
                disabled={isGenerating || isLoading || !messages?.length}
              >
                <RotateCcw className="size-4" />
              </Button>
            </div>
          )}
        </ChatMessageList>
      </ExpandableChatBody>
      <ExpandableChatFooter className="bg-muted/25">
        <form ref={formRef} className="relative flex gap-2" onSubmit={onSubmit}>
          <ChatInput
            value={input}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            className="min-h-12"
            placeholder="Type your message..."
          />
          <Button
            className="absolute top-1/2 right-1 -translate-y-1/2 transform"
            type="submit"
            size="icon"
            disabled={isLoading || isGenerating || !input}
          >
            <Send className="size-4" />
          </Button>
        </form>
      </ExpandableChatFooter>
    </ExpandableChat>
  )
}
